import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  addMonths,
  format,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
} from 'date-fns';
import { CalendarDays } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import '@/styles/ipe/workbench/TimeRangePicker.css';

const WEEKDAY_LABELS = ['\u4e00', '\u4e8c', '\u4e09', '\u56db', '\u4e94', '\u516d', '\u65e5'];

const POPOVER_WIDTH = 660;
const POPOVER_MARGIN = 8;
const YEAR_START = 2000;
const YEAR_END = 2035;

function parseDate(value) {
  if (!value) return null;
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateValue(date) {
  if (!date) return '';
  return format(date, 'yyyy-MM-dd');
}

function startOfMonthDate(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function buildMonthGrid(year, month) {
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startOffset = (firstDay.getDay() + 6) % 7;
  const cells = [];

  for (let i = startOffset - 1; i >= 0; i -= 1) {
    cells.push({
      date: new Date(year, month, -i),
      outside: true,
    });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({
      date: new Date(year, month, day),
      outside: false,
    });
  }

  while (cells.length % 7 !== 0) {
    const dayIndex = cells.length - startOffset - daysInMonth + 1;
    cells.push({
      date: new Date(year, month + 1, dayIndex),
      outside: true,
    });
  }

  while (cells.length < 42) {
    const dayIndex = cells.length - startOffset - daysInMonth + 1;
    cells.push({
      date: new Date(year, month + 1, dayIndex),
      outside: true,
    });
  }

  return cells;
}

function isBetweenRange(date, start, end) {
  if (!start || !end) return false;
  const current = startOfDay(date);
  const rangeStart = startOfDay(start);
  const rangeEnd = startOfDay(end);
  return !isBefore(current, rangeStart) && !isAfter(current, rangeEnd);
}

function MonthGrid({ year, month, rangeStart, rangeEnd, onDayClick }) {
  const { t } = useTranslation('workbench');
  const days = useMemo(() => buildMonthGrid(year, month), [year, month]);
  const weekdayLabels = t('workflowHub.weekdays', { returnObjects: true });
  const labels = Array.isArray(weekdayLabels) ? weekdayLabels : WEEKDAY_LABELS;

  return (
    <div className="time-range-picker__calendar">
      <div className="time-range-picker__weekdays" aria-hidden="true">
        {labels.map((label) => (
          <span key={label} className="time-range-picker__weekday">
            {label}
          </span>
        ))}
      </div>
      <div className="time-range-picker__days" role="grid">
        {days.map(({ date, outside }) => {
          const isStart = rangeStart && isSameDay(date, rangeStart);
          const isEnd = rangeEnd && isSameDay(date, rangeEnd);
          const isEdge = isStart || isEnd;
          const inRange = isBetweenRange(date, rangeStart, rangeEnd);
          const classNames = [
            'time-range-picker__day',
            outside ? 'time-range-picker__day--other-month' : '',
            inRange && !isEdge ? 'time-range-picker__day--in-range' : '',
            isStart ? 'time-range-picker__day--range-start' : '',
            isEnd ? 'time-range-picker__day--range-end' : '',
            isEdge ? 'time-range-picker__day--selected' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={date.toISOString()}
              type="button"
              className={classNames}
              onClick={() => onDayClick(date)}
              aria-label={format(date, 'yyyy-MM-dd')}
              aria-pressed={isEdge}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const YEAR_OPTIONS = Array.from(
  { length: YEAR_END - YEAR_START + 1 },
  (_, index) => YEAR_START + index,
);

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, index) => index);

const YEAR_SELECT_OPTIONS = YEAR_OPTIONS.map((optionYear) => ({
  value: optionYear,
  label: `${optionYear}\u5e74`,
}));

const MONTH_SELECT_OPTIONS = MONTH_OPTIONS.map((optionMonth) => ({
  value: optionMonth,
  label: `${optionMonth + 1}\u6708`,
}));

function PickerSelect({ value, onChange, options, ariaLabel, className = '' }) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  return (
    <div
      ref={rootRef}
      className={`time-range-picker__dropdown${open ? ' time-range-picker__dropdown--open' : ''}${className ? ` ${className}` : ''}`}
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className="time-range-picker__dropdown-trigger"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="time-range-picker__dropdown-value">{selected?.label ?? ''}</span>
        <span className="time-range-picker__dropdown-chevron" aria-hidden />
      </button>
      {open ? (
        <ul className="time-range-picker__dropdown-menu" role="listbox" aria-label={ariaLabel}>
          {options.map((option) => {
            const isActive = option.value === value;
            return (
              <li key={String(option.value)} role="none">
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={`time-range-picker__dropdown-option${isActive ? ' time-range-picker__dropdown-option--active' : ''}`}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  {option.label}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

function CalendarPaneHeader({ viewMonth, onViewMonthChange }) {
  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();

  const setYear = (nextYear) => {
    onViewMonthChange(new Date(nextYear, month, 1));
  };

  const setMonth = (nextMonth) => {
    onViewMonthChange(new Date(year, nextMonth, 1));
  };

  return (
    <div className="time-range-picker__calendar-header">
      <div className="time-range-picker__nav-group">
        <button
          type="button"
          className="time-range-picker__nav-btn"
          onClick={() => onViewMonthChange(addMonths(viewMonth, -12))}
          aria-label={'\u4e0a\u4e00\u5e74'}
        >
          {'\u00ab'}
        </button>
        <button
          type="button"
          className="time-range-picker__nav-btn"
          onClick={() => onViewMonthChange(addMonths(viewMonth, -1))}
          aria-label={'\u4e0a\u4e00\u4e2a\u6708'}
        >
          {'\u2039'}
        </button>
      </div>
      <div className="time-range-picker__selects">
        <PickerSelect
          className="time-range-picker__dropdown--year"
          value={year}
          options={YEAR_SELECT_OPTIONS}
          ariaLabel={'\u9009\u62e9\u5e74\u4efd'}
          onChange={(nextYear) => setYear(Number(nextYear))}
        />
        <PickerSelect
          className="time-range-picker__dropdown--month"
          value={month}
          options={MONTH_SELECT_OPTIONS}
          ariaLabel={'\u9009\u62e9\u6708\u4efd'}
          onChange={(nextMonth) => setMonth(Number(nextMonth))}
        />
      </div>
      <div className="time-range-picker__nav-group">
        <button
          type="button"
          className="time-range-picker__nav-btn"
          onClick={() => onViewMonthChange(addMonths(viewMonth, 1))}
          aria-label={'\u4e0b\u4e00\u4e2a\u6708'}
        >
          {'\u203a'}
        </button>
        <button
          type="button"
          className="time-range-picker__nav-btn"
          onClick={() => onViewMonthChange(addMonths(viewMonth, 12))}
          aria-label={'\u4e0b\u4e00\u5e74'}
        >
          {'\u00bb'}
        </button>
      </div>
    </div>
  );
}

function CalendarPane({ viewMonth, onViewMonthChange, rangeStart, rangeEnd, onDayClick }) {
  return (
    <div className="time-range-picker__calendar-block">
      <CalendarPaneHeader viewMonth={viewMonth} onViewMonthChange={onViewMonthChange} />
      <MonthGrid
        year={viewMonth.getFullYear()}
        month={viewMonth.getMonth()}
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
        onDayClick={onDayClick}
      />
    </div>
  );
}

export default function TimeRangePicker({ startDate = '', endDate = '', onChange, disabled = false }) {
  const { t } = useTranslation('workbench');
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const [open, setOpen] = useState(false);
  const wasOpenRef = useRef(false);
  const [leftViewMonth, setLeftViewMonth] = useState(() => startOfMonthDate(parseDate(startDate) || new Date()));
  const [rightViewMonth, setRightViewMonth] = useState(() => startOfMonthDate(addMonths(parseDate(startDate) || new Date(), 1)));
  const [popoverStyle, setPopoverStyle] = useState(() => ({
    position: 'fixed',
    top: 0,
    left: 0,
    width: POPOVER_WIDTH,
    zIndex: 100000,
  }));

  const parsedStart = useMemo(() => parseDate(startDate), [startDate]);
  const parsedEnd = useMemo(() => parseDate(endDate), [endDate]);

  const formatStartDisplay = (value) => {
    if (!value) return t('workflowHub.dateStart');
    return String(value).trim();
  };

  const formatEndDisplay = (value) => {
    if (!value) return t('workflowHub.dateEnd');
    return String(value).trim();
  };

  const updatePopoverPosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const maxW = Math.min(POPOVER_WIDTH, window.innerWidth - POPOVER_MARGIN * 2);
    let left = rect.left;
    if (left + maxW > window.innerWidth - POPOVER_MARGIN) {
      left = Math.max(POPOVER_MARGIN, window.innerWidth - maxW - POPOVER_MARGIN);
    }
    const top = rect.bottom + POPOVER_MARGIN;
    setPopoverStyle({
      position: 'fixed',
      top,
      left,
      width: maxW,
      zIndex: 100000,
    });
  }, []);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      const anchor = parseDate(startDate) || parseDate(endDate) || new Date();
      const left = startOfMonthDate(anchor);
      setLeftViewMonth(left);
      setRightViewMonth(startOfMonthDate(addMonths(left, 1)));
    }
    wasOpenRef.current = open;
  }, [open, startDate, endDate]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePopoverPosition();
    const onReposition = () => updatePopoverPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updatePopoverPosition]);

  useEffect(() => {
    const onMouseDown = (event) => {
      if (!open) return;
      const target = event.target;
      if (wrapperRef.current?.contains(target) || popoverRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  const emitChange = useCallback((nextStart, nextEnd) => {
    if (typeof onChange !== 'function') return;
    onChange(formatDateValue(nextStart), formatDateValue(nextEnd));
  }, [onChange]);

  const handleDayClick = useCallback((date) => {
    const clicked = startOfDay(date);

    if (!parsedStart || (parsedStart && parsedEnd)) {
      emitChange(clicked, null);
      return;
    }

    if (isBefore(clicked, parsedStart)) {
      emitChange(clicked, parsedStart);
      return;
    }

    emitChange(parsedStart, clicked);
  }, [emitChange, parsedEnd, parsedStart]);

  const hasRange = Boolean(String(startDate || '').trim() || String(endDate || '').trim());

  const clearRange = (event) => {
    event.stopPropagation();
    if (disabled) return;
    emitChange(null, null);
    setOpen(false);
  };

  const popoverContent = open ? (
    <div
      ref={popoverRef}
      className="time-range-picker__popover"
      style={popoverStyle}
      role="dialog"
      aria-label={t('workflowHub.dateRange')}
    >
      <div className="time-range-picker__calendars">
        <CalendarPane
          viewMonth={leftViewMonth}
          onViewMonthChange={setLeftViewMonth}
          rangeStart={parsedStart}
          rangeEnd={parsedEnd}
          onDayClick={handleDayClick}
        />
        <CalendarPane
          viewMonth={rightViewMonth}
          onViewMonthChange={setRightViewMonth}
          rangeStart={parsedStart}
          rangeEnd={parsedEnd}
          onDayClick={handleDayClick}
        />
      </div>
    </div>
  ) : null;

  return (
    <div className={`time-range-picker${disabled ? ' time-range-picker--disabled' : ''}`} ref={wrapperRef}>
      <div
        ref={triggerRef}
        className={`time-range-picker__wrapper${open ? ' time-range-picker__wrapper--open' : ''}`}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
        onKeyDown={(event) => {
          if (disabled) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen((v) => !v);
          }
        }}
        aria-label={t('workflowHub.dateRange')}
        aria-expanded={open}
        aria-disabled={disabled}
      >
        <div className="time-range-picker__inputs">
          <span
            className={`time-range-picker__input${startDate ? '' : ' time-range-picker__input--placeholder'}`}
          >
            {formatStartDisplay(startDate)}
          </span>
          <span className="time-range-picker__separator">~</span>
          <span
            className={`time-range-picker__input${endDate ? '' : ' time-range-picker__input--placeholder'}`}
          >
            {formatEndDisplay(endDate)}
          </span>
        </div>
        <button
          type="button"
          className="time-range-picker__icon-btn"
          disabled={disabled}
          onClick={(event) => {
            event.stopPropagation();
            if (disabled) return;
            setOpen((v) => !v);
          }}
          aria-label={t('workflowHub.openPicker')}
        >
          <CalendarDays size={16} strokeWidth={2} aria-hidden="true" />
        </button>
        {hasRange && (
          <button
            type="button"
            className="time-range-picker__clear-btn"
            disabled={disabled}
            onClick={clearRange}
            aria-label={t('workflowHub.clearRange')}
          >
            {'\u00d7'}
          </button>
        )}
      </div>
      {typeof document !== 'undefined' && popoverContent
        ? createPortal(popoverContent, document.body)
        : null}
    </div>
  );
}
